import React, { FunctionComponent, useEffect } from 'react';
import { connect, DispatchProp } from 'react-redux';
import { ICloudProvider } from './types';
import { setCloudProvidersList, setCloudProvider } from 'Quotes/actions';
import { IAppState } from 'reducers';
import CloudProvidersCapture from './CloudProvidersCapture';
import { ProviderName } from 'Quotes/types/store';
import { OnChange } from 'Quotes/types/availabilityCheck';
import { ProductType } from 'Quotes/types/productTypes';

export interface ICloudProviders {
  className?: string;
  cloudProvidersList: ICloudProvider[];
  selectedProviderName: ProviderName;
  initialisedFromData?: boolean;
  onCloudProvidersFetchSuccess(cloudProviders: ICloudProvider[]): void;
  onCloudProviderSelected(cloudProvider: ICloudProvider): void;
  onAvailabilityCheckChange?: OnChange;
  customerId?: string;
}

const CloudProviders: FunctionComponent<React.PropsWithChildren<ICloudProviders>> = ({
  className,
  cloudProvidersList,
  onCloudProvidersFetchSuccess,
  onCloudProviderSelected,
  selectedProviderName,
  onAvailabilityCheckChange,
  initialisedFromData = false,
  customerId,
}) => {
  const onSelected = (cloudProvider: ICloudProvider) => {
    onCloudProviderSelected(cloudProvider);

    if (typeof onAvailabilityCheckChange === 'function') {
      onAvailabilityCheckChange(
        {
          cloud_connect_provider: cloudProvider.attributes.name,
        },
        'cloud'
      );
    }
  };

  useEffect(() => {
    if (typeof onAvailabilityCheckChange === 'function' && initialisedFromData) {
      onAvailabilityCheckChange(
        {
          cloud_connect_provider: selectedProviderName,
        },
        'cloud'
      );
    }
  }, []);

  return (
    <div className={className}>
      <CloudProvidersCapture
        cloudProvidersList={cloudProvidersList}
        selectedProviderName={selectedProviderName}
        onCloudProvidersFetchSuccess={onCloudProvidersFetchSuccess}
        onCloudProviderSelected={onSelected}
        customerId={customerId}
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch: DispatchProp['dispatch']) => ({
  onCloudProvidersFetchSuccess: (cloudProvidersList: ICloudProvider[]) =>
    dispatch(setCloudProvidersList(cloudProvidersList)),
  onCloudProviderSelected: (cloudProvider: ICloudProvider) => dispatch(setCloudProvider(cloudProvider)),
});

const mapStateToProps = (state: IAppState) => {
  let cloudProviders = state.quoteBuilder.quote.location.aEnd.cloudConnect.providersList;

  // Cerillion has not enabled all cloud providers for NNI2CCT and P2CCT products
  if (state.quoteBuilder.quote.productType === ProductType.P2CCT || state.quoteBuilder.quote.productType === ProductType.NNI2CCT) {
    cloudProviders = cloudProviders.filter(provider => provider.attributes.name === ProviderName.AWS || provider.attributes.name === ProviderName.Azure);
  }

  return {
    cloudProvidersList: cloudProviders,
    selectedProviderName: state.quoteBuilder.quote.location.aEnd.cloudConnect.name,
    customerId: state.user.companyData.selectedCompanyId ?? undefined,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CloudProviders);
